import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";

interface SalesData {
  [key: string]: string | number;
}

interface CompanyConfig {
  name: string;
  dataKey: string;
  color: string;
}

interface SalesChartProps {
  data: SalesData[];
  companies?: CompanyConfig[];
  xAxisKey?: string;
  yDomain?: [number, number];
  yTicks?: number[];
}

// 색상 팔레트 (20종 이상)
const DEFAULT_COLORS = [
  "#52C41A", // 녹색
  "#FA8C16", // 주황색
  "#1890FF", // 파란색
  "#FADB14", // 노란색
  "#CF1322", // 빨간색
  "#722ED1", // 보라색
  "#EB2F96", // 핑크색
  "#13C2C2", // 청록색
  "#F759AB", // 핑크 자주
  "#FF7875", // 산호색
  "#FFA940", // 오렌지
  "#A0D911", // 라임 그린
  "#389E0D", // 다크 그린
  "#0958D9", // 네이비 블루
  "#2F54EB", // 로열 블루
  "#9254DE", // 퍼플
  "#B37FEB", // 라벤더
  "#FF85C0", // 로즈 핑크
  "#FF4D4F", // 레드
  "#FAAD14", // 앰버
  "#73D13D", // 라이트 그린
  "#36CFC9", // 시안
  "#597EF7", // 인디고
  "#B04DCC", // 바이올렛
];

export default function SalesChart({ data, companies, xAxisKey = "month", yDomain, yTicks }: SalesChartProps) {
  // companies가 제공되지 않으면 데이터에서 자동 감지
  const autoCompanies: CompanyConfig[] =
    companies ||
    (() => {
      if (!data || data.length === 0) return [];

      const firstRow = data[0];
      const keys = Object.keys(firstRow).filter(key => key !== xAxisKey);

      return keys.map((key, index) => ({
        name: key,
        dataKey: key,
        color: DEFAULT_COLORS[index % DEFAULT_COLORS.length],
      }));
    })();

  return (
    <div className="w-full h-full p-4">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={data}
          margin={{
            top: 20,
            right: 0,
            left: 40,
            bottom: 20,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={true} stroke="#E8E8E8" />
          <XAxis
            dataKey={xAxisKey}
            tick={{ fill: "#666" }}
            axisLine={{ stroke: "#E8E8E8" }}
            tickLine={{ stroke: "#E8E8E8" }}
          />
          <YAxis
            domain={yDomain || [0, 100]}
            ticks={yTicks}
            tick={{ fill: "#666" }}
            axisLine={{ stroke: "#E8E8E8" }}
            tickLine={{ stroke: "#E8E8E8" }}
            tickFormatter={value => value.toLocaleString()}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: "#fff",
              border: "1px solid #E8E8E8",
              borderRadius: "4px",
            }}
            formatter={(value: number) => value.toLocaleString()}
          />
          <Legend wrapperStyle={{ paddingTop: "20px" }} iconType="line" align="center" />
          {autoCompanies.map(company => (
            <Line
              key={company.dataKey}
              type="monotone"
              dataKey={company.dataKey}
              name={company.name}
              stroke={company.color}
              strokeWidth={3}
              dot={false}
              activeDot={{ r: 6 }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
